import React, { useState, useEffect } from "react";
import {
  Github,
  Check,
  AlertCircle,
  RefreshCw,
  ExternalLink,
  LogOut,
} from "lucide-react";
import api from "../api/client";

function GitHubIntegration() {
  const [status, setStatus] = useState({
    connected: false,
    username: null,
    repo: null,
  });
  const [loading, setLoading] = useState(true);
  const [repos, setRepos] = useState([]);
  const [selectedRepo, setSelectedRepo] = useState("");
  const [message, setMessage] = useState(null);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    // Check URL params
    const params = new URLSearchParams(window.location.search);
    if (params.get("github") === "connected") {
      setMessage({ type: "success", text: "GitHub connected successfully!" });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    if (params.get("error")) {
      setMessage({
        type: "error",
        text: `GitHub connection failed: ${params.get("error")}`,
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/v1/github/status");
      setStatus(response.data);
      if (response.data.connected) {
        await fetchRepos();
        if (response.data.repo) {
          setSelectedRepo(response.data.repo);
        }
      }
    } catch (error) {
      console.error("Error fetching status:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRepos = async () => {
    try {
      const response = await api.get("/api/v1/github/repos");
      setRepos(response.data || []);
      if (response.data.length > 0 && !selectedRepo) {
        setSelectedRepo(response.data[0].name);
      }
    } catch (error) {
      console.error("Error fetching repos:", error);
    }
  };

  const handleConnect = async () => {
    if (connecting) return;

    try {
      setConnecting(true);
      setMessage(null);

      const response = await api.get("/api/v1/github/auth");
      const authUrl = response.data.auth_url;

      if (!authUrl) {
        setMessage({ type: "error", text: "Failed to get OAuth URL" });
        setConnecting(false);
        return;
      }

      window.open(authUrl, "_blank");

      setMessage({
        type: "info",
        text: "✅ A new tab has been opened. Please authorize FrankTech on GitHub, then close the tab and refresh this page.",
      });

      setTimeout(() => {
        fetchStatus();
      }, 5000);
    } catch (error) {
      console.error("Error connecting GitHub:", error);
      setMessage({
        type: "error",
        text:
          error.response?.data?.detail ||
          "Failed to connect GitHub. Please try again.",
      });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Are you sure you want to disconnect GitHub?")) return;

    try {
      setLoading(true);
      await api.post("/api/v1/github/disconnect");
      setStatus({ connected: false, username: null, repo: null });
      setRepos([]);
      setSelectedRepo("");
      setMessage({
        type: "success",
        text: "GitHub disconnected successfully!",
      });
      await fetchStatus();
    } catch (error) {
      console.error("Error disconnecting:", error);
      setMessage({ type: "error", text: "Failed to disconnect" });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRepo = async () => {
    if (!selectedRepo) return;

    try {
      setLoading(true);
      await api.post("/api/v1/github/repository", { repo: selectedRepo });
      setMessage({ type: "success", text: "Repository saved!" });
      await fetchStatus();
    } catch (error) {
      console.error("Error saving repo:", error);
      setMessage({ type: "error", text: "Failed to save repository" });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
          <Github className="w-5 h-5 text-cyan-400" />
        </div>
        <div>
          <h3 className="text-white font-semibold">GitHub Integration</h3>
          <p className="text-gray-400 text-sm">
            Connect GitHub to create PRs with AI fixes
          </p>
        </div>
      </div>

      {/* Status */}
      <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg border border-gray-700/50">
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
              status.connected
                ? "bg-green-500/20 text-green-400 border border-green-500/30"
                : "bg-gray-700/50 text-gray-400 border border-gray-600/50"
            }`}
          >
            {status.connected ? (
              <>
                <Check className="w-3 h-3" /> Connected
              </>
            ) : (
              <>
                <AlertCircle className="w-3 h-3" /> Not Connected
              </>
            )}
          </span>
          {status.username && (
            <span className="text-gray-400 text-sm">
              as{" "}
              <span className="text-white font-mono">@{status.username}</span>
            </span>
          )}
          {status.repo && (
            <span className="text-gray-400 text-sm">
              Repo: <span className="text-white font-mono">{status.repo}</span>
            </span>
          )}
        </div>

        {status.connected && (
          <button
            onClick={handleDisconnect}
            className="text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 px-3 py-1 rounded-lg transition-colors"
          >
            Disconnect
          </button>
        )}
      </div>

      {/* Messages */}
      {message && (
        <div
          className={`p-3 rounded-lg text-sm ${
            message.type === "success"
              ? "bg-green-500/10 border border-green-500/20 text-green-400"
              : message.type === "error"
                ? "bg-red-500/10 border border-red-500/20 text-red-400"
                : "bg-blue-500/10 border border-blue-500/20 text-blue-400"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Connect Button - SMALLER */}
      {!status.connected && (
        <button
          onClick={handleConnect}
          disabled={connecting}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-200 hover:scale-[1.02] disabled:opacity-50 text-sm"
        >
          {connecting ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Connecting...
            </>
          ) : (
            <>
              <Github className="w-4 h-4" /> Connect GitHub
            </>
          )}
        </button>
      )}

      {/* Repository Selection */}
      {status.connected && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Select Repository
            </label>
            <select
              value={selectedRepo}
              onChange={(e) => setSelectedRepo(e.target.value)}
              className="w-full bg-gray-900/50 border border-gray-800/50 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-cyan-500"
            >
              {repos.length === 0 ? (
                <option>Loading repositories...</option>
              ) : (
                repos.map((repo) => (
                  <option key={repo.name} value={repo.name}>
                    {repo.name}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Save Repository Button - SMALLER */}
          <button
            onClick={handleSaveRepo}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-200 hover:scale-[1.02] text-sm"
          >
            <Check className="w-4 h-4" /> Save Repository
          </button>

          <div className="flex items-center gap-1 text-xs text-gray-500">
            <ExternalLink className="w-3 h-3" />
            PRs will be created in:{" "}
            <span className="text-cyan-400">
              {selectedRepo || "No repository selected"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default GitHubIntegration;
